import React, { useEffect, useRef, useState } from "react";
import {
  useTheme,
  CssBaseline,
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Switch,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { baseapi } from "../Environmentalurl";
import {
  date,
  entity_id,
  orderID,
} from "../Utils/Const";
import { useNavigate } from "react-router-dom";
import useIPAddress from "../Utils/useIPAddress";
import api from "../Utils/Api";
import DashboardHeader from "../Dashboard/DashboardHeader";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";

// ─── APSPDCL-specific constants ──────────────────────────────────────────────
// Platform = "2" per the APSPDCL integration doc
// (different from other BBPS services that use Platform = "1")
const APSPDCL_PLATFORM = "1";

// ─── Response code helper ─────────────────────────────────────────────────────
// Per doc: 4-digit codes = success, 5-digit codes = failure
const isSuccess = (code) => code && String(code).length === 4;

// ─── Capture GeoLocation from browser ────────────────────────────────────────
// GeoLocation is MANDATORY for both Step 1 and Step 2 per APSPDCL doc.
// Returns "latitude,longitude" string, or empty string on denial/error.
const captureGeoLocation = () =>
  new Promise((resolve) => {
    // First check if we already have a cached value from this session
    const cached = sessionStorage.getItem("geoLocation");
    if (cached) { resolve(cached); return; }

    if (!navigator.geolocation) { resolve(""); return; }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const geo = `${pos.coords.latitude.toFixed(4)},${pos.coords.longitude.toFixed(4)}`;
        sessionStorage.setItem("geoLocation", geo);
        resolve(geo);
      },
      () => resolve(""),
      { timeout: 5000, maximumAge: 60000, enableHighAccuracy: false },
    );
  });

export default function APSPDCL() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const md = useMediaQuery("(min-width:900px)");
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const ip = useIPAddress();

  const mobile = JSON.parse(sessionStorage.getItem("mobileNumber"));
  const session = JSON.parse(sessionStorage.getItem("SessionID"));

  // ── Location state ────────────────────────────────────────────────────────
  const [geoLocation, setGeoLocation] = useState(
    sessionStorage.getItem("geoLocation") || "",
  );

  // ── Form state ────────────────────────────────────────────────────────────
  const [scno, setScno] = useState("");
  const [scnoError, setScnoError] = useState("");
  const [isAdvance, setIsAdvance] = useState(false);
  const [advanceAmount, setAdvanceAmount] = useState("");

  // ── Loading ───────────────────────────────────────────────────────────────
  const [fetchLoading, setFetchLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(false);

  // ── Bill data ─────────────────────────────────────────────────────────────
  const [billData, setBillData] = useState(null);
  const [billDialogOpen, setBillDialogOpen] = useState(false);

  // ── TPIN (4–6 digits for APSPDCL, doc regex: ^\d{4,6}$) ─────────────────
  const [tpinOpen, setTpinOpen] = useState(false);
  const [tpin, setTpin] = useState("");
  const tpinRef = useRef(null);

  // ── Payment result ────────────────────────────────────────────────────────
  const [paymentResult, setPaymentResult] = useState({
    open: false,
    success: false,
    message: "",
    transactionId: "",
    requestId: "",
  });

  // ── Insufficient balance ──────────────────────────────────────────────────
  const [insufficientOpen, setInsufficientOpen] = useState(false);
  const [insufficientMsg, setInsufficientMsg] = useState("");

  // ── Capture geo on mount ──────────────────────────────────────────────────
  useEffect(() => {
    if (!geoLocation) {
      captureGeoLocation().then((geo) => {
        if (geo) {
          setGeoLocation(geo);
        }
      });
    }
  }, []);
  // ── SCNO validation (regex: ^\d{13}$) ────────────────────────────────────
  const validateScno = (val) => {
    if (!val) return "Consumer Number is required";
    if (!/^\d{13}$/.test(val)) return "Consumer Number must be exactly 13 digits";
    return "";
  };

  // ── Step 1: Fetch Bill ────────────────────────────────────────────────────
  const handleFetchBill = async () => {
    const scnoErr = validateScno(scno);
    if (scnoErr) { setScnoError(scnoErr); return; }
    setScnoError("");

    // GeoLocation — capture if not already available
    if (!geoLocation) {
      const geo = await captureGeoLocation();
      if (geo) setGeoLocation(geo);
    }

    setFetchLoading(true);

    try {
      const response = await api.post(`${baseapi}/apspdcl/bill-fetch`, {
        ReqType: "APSPDCL_BillFetch",
        Platform: APSPDCL_PLATFORM,
        EntityID: entity_id,
        MachineID: ip,
        Mobilenumber: mobile,
        SessionID: session,
        Date: date,
        SCNO: scno,
        GeoLocation: geoLocation,   // MANDATORY
      });

      const data = response.data;

      if (isSuccess(data.Response_Code) && data.Info?.STATUS === "VALID") {
        setBillData(data.Info);
        setBillDialogOpen(true);
      } else {
        setPaymentResult({
          open: true,
          success: false,
          message:
            data.Message ||
            "Bill fetch failed. Please verify your Consumer Number and try again.",
          transactionId: "",
          requestId: "",
        });
      }
    } catch (err) {
      console.error("APSPDCL bill fetch error:", err);
      setPaymentResult({
        open: true,
        success: false,
        message: "Failed to fetch bill. Please try again.",
        transactionId: "",
        requestId: "",
      });
    } finally {
      setFetchLoading(false);
    }
  };

  // ── Bill dialog "Pay Now" → validate → open TPIN ─────────────────────────
  const handlePayNow = () => {
    if (isAdvance && (!advanceAmount || Number(advanceAmount) <= 0)) {
      alert("Please enter a valid advance payment amount");
      return;
    }
    setBillDialogOpen(false);
    setTpin("");
    setTpinOpen(true);
    setTimeout(() => tpinRef.current?.focus(), 100);
  };


  const handlePayment = async () => {
    // APSPDCL TPIN: 4–6 digits (regex: ^\d{4,6}$)
    if (!/^\d{4,6}$/.test(tpin)) {
      alert("Please enter a valid 4 to 6 digit TPIN");
      return;
    }
    if (!billData) {
      alert("Bill data missing. Please fetch the bill again.");
      return;
    }

    // Re-capture geo at payment time (doc says GeoLocation mandatory in Step 2)
    const payGeo = geoLocation || (await captureGeoLocation());

    setPayLoading(true);

    const payAmount = isAdvance ? advanceAmount : billData.TOTAMOUNT;

    try {
      const response = await api.post(`${baseapi}/apspdcl/bill-pay`, {
        ReqType: "APSPDCL_Paybill",
        Platform: APSPDCL_PLATFORM,
        EntityID: entity_id,
        MachineID: ip,
        Mobilenumber: mobile,
        SessionID: session,
        OrderID: orderID,
        Date: date,
        TPIN: tpin,
        GeoLocation: payGeo,
        SCNO: billData.SCNO,
        SCTYPE: billData.SCTYPE,
        Circle: billData.CIRCLENAME,
        ERO: billData.EROCODE,
        CONSUMERNAME: billData.CONSUMERNAME,
        ARREARS: billData.ARREARS,
        CURRENTDEMAND: isAdvance ? advanceAmount : billData.CURRENTDEMAND,
        ACDAMT: billData.ACDAMT,
        RCFEES: billData.RCFEES,
        CHECKOUTAMOUNT: payAmount,
        Amount: payAmount,
        RDGDATE: billData.RDGDATE,
        DUEDATE: billData.DUEDATE,
        DISCDATE: billData.DISCDATE,
        CHECKSUM: billData.CHECKSUM,
        PAYMODE: "DEBIT",
        PAYCHANNEL: "PGW",
        AdvancePayment: isAdvance ? "1" : "0",
        ServiceID: "26"
      });

      const data = response.data;
      setTpinOpen(false);

      if (isSuccess(data.Response_Code)) {
        setPaymentResult({
          open: true,
          success: true,
          message: data.Message || "Payment successful",
          transactionId: data.TRANSACTIONID || "",
          requestId: data.RequestID || "",
        });
      } else if (
        data.Response_Code === "2001" ||
        (data.Message || "").toLowerCase().includes("insufficient")
      ) {
        setInsufficientMsg(data.Message || "Insufficient Wallet Balance");
        setInsufficientOpen(true);
      } else {
        setPaymentResult({
          open: true,
          success: false,
          message: data.Message || "Payment failed. Please try again.",
          transactionId: "",
          requestId: "",
        });
      }
    } catch (err) {
      console.error("APSPDCL bill payment error:", err);
      setTpinOpen(false);
      setPaymentResult({
        open: true,
        success: false,
        message: "Payment processing failed. Please try again.",
        transactionId: "",
        requestId: "",
      });
    } finally {
      setPayLoading(false);
    }
  };

  const BillRow = ({ label, value, highlight }) => (
    <Box display="flex" justifyContent="space-between" mb={1}>
      <Typography fontSize={13} color="text.secondary">{label}</Typography>
      <Typography fontSize={13} fontWeight={highlight ? 700 : 600} color={highlight ? "#16a34a" : "text.primary"}>
        {value || "—"}
      </Typography>
    </Box>
  );

  return (
    <>
      <DashboardHeader />
      <CssBaseline />
      <Box
        sx={{
          background: "linear-gradient(135deg, #16a34a, #22c55e)",
          color: "#fff",
          px: { xs: 2, md: 4 },
          py: { xs: 2.5, md: 3 },
          borderBottomLeftRadius: 24,
          borderBottomRightRadius: 24,
          boxShadow: "0 6px 20px rgba(22,163,74,0.25)",
        }}
      >
        <Box
          sx={{
            maxWidth: 1300,
            mx: "auto",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          {/* Left */}
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.15)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <BoltIcon sx={{ fontSize: 34, color: "#FFD54F" }} />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 22, md: 30 },
                  lineHeight: 1.2,
                }}
              >
                APSPDCL Bill Payment
              </Typography>

              <Typography
                sx={{
                  opacity: 0.9,
                  fontSize: { xs: 13, md: 15 },
                }}
              >
                Andhra Pradesh Southern Power Distribution Company
              </Typography>
            </Box>
          </Box>

          {/* Right */}
          <Button
            variant="contained"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate("/dashboard")}
            sx={{
              bgcolor: "#fff",
              color: "#ffffff",
              fontWeight: 700,
              borderRadius: 3,
              px: 3,
              py: 1.2,
              boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
              "&:hover": {
                bgcolor: "#f3f4f6",
              },
            }}
          >
            Back to Home
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          background: "#f8fafc",
          px: { xs: 2, md: 4 },
          py: 5,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1300 }}>
          <Grid container spacing={3}>

            {/* ── Left Card ─────────────────────────────────────────────────── */}
            <Grid size={{ xs: 12, md: 4 }} item xs={12} md={4}>
              <Card elevation={isDark ? 0 : 2} sx={{ background: "white", borderRadius: 3, p: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.04)", border: "1px solid #f0f0f0" }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <BoltIcon sx={{ color: "#f59e0b", fontSize: 28 }} />
                    <Typography variant="h5" sx={{ color: theme.palette.primary.main, fontWeight: 700, fontSize: { xs: 15 } }}>
                      APSPDCL Electricity Bill
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
                    Andhra Pradesh Southern Power Distribution Company
                  </Typography>

                  {/* SCNO field */}
                  <Typography variant="caption" color="text.secondary">
                    Consumer Number (SCNO) *
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter 13-digit Consumer Number"
                    variant="filled"
                    autoComplete="off"
                    value={scno}
                    inputProps={{ maxLength: 13, inputMode: "numeric" }}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      setScno(val);
                      if (scnoError) setScnoError(validateScno(val));
                    }}
                    error={!!scnoError}
                    helperText={scnoError || `${scno.length}/13 digits`}
                    sx={{
                      mt: 1, mb: 2, borderRadius: 1,
                      "& .MuiFilledInput-root": { backgroundColor: isDark ? "#12303a" : "#f0f0f0", border: isDark ? "#12303a" : "1px solid #999", borderRadius: 1 },
                    }}
                  />

                  {/* Advance payment toggle */}
                  <FormControlLabel
                    control={<Switch checked={isAdvance} onChange={(e) => { setIsAdvance(e.target.checked); setAdvanceAmount(""); }} color="primary" size="small" />}
                    label={<Typography variant="body2">Advance Payment</Typography>}
                    sx={{ mb: 1 }}
                  />

                  {isAdvance && (
                    <>
                      <Typography variant="caption" color="text.secondary">Advance Amount</Typography>
                      <TextField
                        fullWidth type="number" placeholder="Enter advance amount" variant="filled" autoComplete="off"
                        value={advanceAmount} onChange={(e) => setAdvanceAmount(e.target.value)}
                        sx={{ mt: 1, mb: 2, borderRadius: 1, "& .MuiFilledInput-root": { backgroundColor: isDark ? "#12303a" : "#f0f0f0", border: isDark ? "#12303a" : "1px solid #999", borderRadius: 1 } }}
                      />
                    </>
                  )}

                  <Button
                    fullWidth size="large"
                    endIcon={fetchLoading ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />}
                    onClick={handleFetchBill}
                    disabled={fetchLoading}
                    sx={{ background: "linear-gradient(135deg, #16a34a, #22c55e)", color: "#fff", borderRadius: 2, py: 1.2, fontWeight: 700, textTransform: "none" }}
                  >
                    {fetchLoading ? "Fetching Bill..." : "Fetch Bill"}
                  </Button>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 3, color: "text.secondary" }}>
                    <ShieldOutlinedIcon sx={{ color: theme.palette.primary.main }} />
                    <Typography variant="body2">100% Secure & Instant Payment</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* ── Right Card ─────────────────────────────────────────────────── */}
            {!isMobile && (
              <Grid size={{ xs: 12, md: 8 }} item xs={12} md={8}>
                <Card elevation={isDark ? 0 : 2} sx={{ background: "white", borderRadius: 3, p: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.04)", height: "100%", border: "1px solid #f0f0f0" }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: "text.primary", fontWeight: 700, mb: 2 }}>About APSPDCL</Typography>
                    <Box sx={{ background: isDark ? "#12303a" : "rgba(0,0,0,0.04)", borderRadius: 2, p: 3, color: "text.secondary", mb: 2 }}>
                      <Typography variant="body2" mb={1}>
                        Pay your APSPDCL (Southern AP) electricity bill instantly. Enter your 13-digit Consumer Number (SCNO) to fetch your bill.
                      </Typography>
                      <Typography variant="body2">
                        Location access is required for security verification. Enable GPS on your device for a smooth experience.
                      </Typography>
                    </Box>
                    {/* <Box sx={{ background: isDark ? "#0f3a35" : "rgba(0,123,255,0.05)", borderRadius: 1.5, p: 1.2 }}>
                      <Typography sx={{ fontWeight: 600, color: theme.palette.primary.main }}>Disclaimer:</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Verify the consumer number before proceeding. Transactions once initiated cannot be reversed.
                      </Typography>
                    </Box> */}
                    <Box
                      sx={{
                        background: isDark ? "#0f3a35" : "rgba(0,123,255,0.05)",
                        borderRadius: 2,
                        p: 2,
                        mt: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.primary.main,
                          mb: 2,
                        }}
                      >
                        Transaction fees for various modes of payment are as follows:
                      </Typography>

                      <TableContainer
                        component={Paper}
                        sx={{
                          border: "1px solid #d1d5db",
                          borderRadius: 2,
                          overflow: "hidden",
                        }}
                      >
                        <Table
                          size="small"
                          sx={{
                            "& .MuiTableCell-root": {
                              border: "1px solid #d1d5db",
                            },
                          }}
                        >
                          <TableHead>
                            <TableRow sx={{ backgroundColor: "#16a34a" }}>
                              <TableCell
                                sx={{
                                  color: "#fff",
                                  fontWeight: 700,
                                  border: "1px solid #d1d5db",
                                }}
                              >
                                Payment Mode
                              </TableCell>

                              <TableCell
                                sx={{
                                  color: "#fff",
                                  fontWeight: 700,
                                  border: "1px solid #d1d5db",
                                }}
                              >
                                Transaction Fee
                              </TableCell>
                            </TableRow>
                          </TableHead>

                          <TableBody>
                            <TableRow>
                              <TableCell>TA Wallet</TableCell>
                              <TableCell>0.75% of Transaction Amount + GST</TableCell>
                            </TableRow>

                            <TableRow>
                              <TableCell>Debit Card</TableCell>
                              <TableCell>
                                 Rupay: NIL
                                <br />
                                 Visa & Master: 0.85% of Transaction Amount + GST
                              </TableCell>
                            </TableRow>

                            <TableRow>
                              <TableCell>Credit Card</TableCell>
                              <TableCell>0.95 % of the Transaction Amount + GST</TableCell>
                            </TableRow>

                            <TableRow>
                              <TableCell>Net Banking</TableCell>
                              <TableCell>1.6 % of the Transaction Amount + GST</TableCell>
                            </TableRow>

                            <TableRow>
                              <TableCell>UPI (Unified Payment Interface)</TableCell>
                              <TableCell>NIL</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 2, fontStyle: "italic" }}
                      >
                        * GST and other applicable Government taxes will be charged extra, wherever
                        applicable.
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>

      {/* ── BILL DETAILS DIALOG ──────────────────────────────────────────────── */}
      <Dialog
        open={billDialogOpen}
        onClose={() => setBillDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden" } }}
      >
        {/* Header */}
        <Box sx={{ background: "linear-gradient(135deg, #f59e0b, #d97706)", py: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
          <BoltIcon sx={{ color: "#fff", fontSize: 36, mb: 0.5 }} />
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>APSPDCL Bill Details</Typography>
        </Box>

        <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
          {billData && (
            <Box sx={{ bgcolor: "#f5f7fb", borderRadius: 2, p: 2.5 }}>
              {Object.entries(billData)
                .filter(
                  ([key]) =>
                    key !== "STATUS" &&
                    key !== "CHECKSUM"
                )
                .map(([key, value]) => (
                  <Box
                    key={key}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1.5}
                  >
                    <Typography
                      fontSize={14}
                      color="text.secondary"
                      fontWeight={600}
                    >
                      {key
                        .replace(/^./, (str) => str.toUpperCase())}
                    </Typography>

                    <Typography
                      fontSize={14}
                      fontWeight={700}
                      sx={{
                        color:
                          key === "DUEDATE"
                            ? "#e65100"
                            : "text.primary",
                      }}
                    >
                      {value || "-"}
                    </Typography>
                  </Box>
                ))}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 2, flexDirection: "column", gap: 1 }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handlePayNow}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 700,
              fontSize: 15,
              py: 1.3,
              background: "linear-gradient(135deg, #16a34a, #22c55e)",
              boxShadow: "0 6px 16px rgba(22,163,74,0.35)",
            }}
          >
            Pay Bill
          </Button>
          <Button
            fullWidth
            onClick={() => setBillDialogOpen(false)}
            sx={{ textTransform: "none", borderRadius: 2, color: "text.secondary" }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── TPIN DIALOG ─────────────────────────────────────────────────────────── */}
      <Dialog open={tpinOpen} onClose={() => setTpinOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: "center", fontWeight: 700 }}>🔐 Enter TPIN</DialogTitle>
        <DialogContent dividers>
          <Typography fontSize={12} color="text.secondary" textAlign="center" mb={2}>
            Enter your 4 digits TPIN to confirm payment
          </Typography>
          <Box display="flex" justifyContent="center">
            <TextField
              inputRef={tpinRef}
              type="password"
              value={tpin}
              inputProps={{ maxLength: 4, inputMode: "numeric", style: { textAlign: "center", fontSize: 22, fontWeight: 700, letterSpacing: 8 } }}
              onChange={(e) => setTpin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              sx={{ width: 220, "& .MuiOutlinedInput-root": { height: 60, borderRadius: 3 } }}
            />
          </Box>
          <Typography fontSize={11} color="text.secondary" textAlign="center" mt={1}>
            {tpin.length}/4 digits entered
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTpinOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained" fullWidth
            disabled={tpin.length < 4 || payLoading}
            onClick={handlePayment}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 700, background: "linear-gradient(135deg, #16a34a, #22c55e)" }}
          >
            {payLoading ? "Processing..." : "Confirm Payment"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── INSUFFICIENT BALANCE ──────────────────────────────────────────────── */}
      <Dialog open={insufficientOpen} onClose={() => setInsufficientOpen(false)} PaperProps={{ sx: { borderRadius: 3, p: 1, width: 380 } }}>
        <DialogTitle sx={{ textAlign: "center", fontWeight: 600, fontSize: 18, pb: 0.5 }}>⚠️ Insufficient Balance</DialogTitle>
        <DialogContent sx={{ textAlign: "center", mt: 1 }}>
          <Typography sx={{ color: "text.secondary", fontSize: 14 }}>{insufficientMsg}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setInsufficientOpen(false)} fullWidth variant="outlined" sx={{ borderRadius: 2, textTransform: "none" }}>Cancel</Button>
          <Button fullWidth variant="contained" onClick={() => { setInsufficientOpen(false); navigate("/dashboard/loadmoney"); }} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, ml: 1, color: "white" }}>Load Money</Button>
        </DialogActions>
      </Dialog>

      {/* ── PAYMENT RESULT DIALOG ─────────────────────────────────────────────── */}
      <Dialog
        open={paymentResult.open}
        onClose={() => setPaymentResult({ ...paymentResult, open: false })}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden" } }}
      >
        <Box sx={{
          background: paymentResult.success ? "linear-gradient(135deg, #16a34a, #22c55e)" : "linear-gradient(135deg, #dc2626, #b91c1c)",
          py: 3.5, display: "flex", flexDirection: "column", alignItems: "center", gap: 1,
        }}>
          <Typography sx={{ fontSize: 40 }}>{paymentResult.success ? "🎉" : "❌"}</Typography>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>
            {paymentResult.success ? "Payment Successful!" : "Payment Failed"}
          </Typography>
        </Box>

        <DialogContent sx={{ px: 3, pt: 2.5, pb: 1, textAlign: "center" }}>
          <Typography sx={{ fontSize: 14, color: "text.secondary", lineHeight: 1.6, mb: 1.5 }}>
            {paymentResult.message}
          </Typography>

          {paymentResult.success && (
            <Box sx={{ bgcolor: "#f5f7fb", borderRadius: 2, p: 2, textAlign: "left" }}>
              {paymentResult.transactionId && (
                <Box display="flex" justifyContent="space-between" mb={0.8}>
                  <Typography fontSize={13} color="text.secondary">Transaction ID</Typography>
                  <Typography fontSize={13} fontWeight={700}>{paymentResult.transactionId}</Typography>
                </Box>
              )}
              {paymentResult.requestId && (
                <Box display="flex" justifyContent="space-between" mb={0.8}>
                  <Typography fontSize={13} color="text.secondary">Request ID</Typography>
                  <Typography fontSize={13} fontWeight={700}>{paymentResult.requestId}</Typography>
                </Box>
              )}
              {billData && (
                <>
                  <Box display="flex" justifyContent="space-between" mb={0.8}>
                    <Typography fontSize={13} color="text.secondary">Consumer</Typography>
                    <Typography fontSize={13} fontWeight={600}>{billData.CONSUMERNAME}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={0.8}>
                    <Typography fontSize={13} color="text.secondary">SCNO</Typography>
                    <Typography fontSize={13} fontWeight={600}>{billData.SCNO}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={0.8}>
                    <Typography fontSize={13} color="text.secondary">Due Date</Typography>
                    <Typography fontSize={13} fontWeight={600}>{billData.DUEDATE}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography fontSize={13} color="text.secondary">Amount Paid</Typography>
                    <Typography fontSize={13} fontWeight={700} color="#16a34a">
                      ₹{isAdvance ? advanceAmount : billData.TOTAMOUNT}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            variant="contained" fullWidth size="large"
            onClick={() => {
              setPaymentResult({ ...paymentResult, open: false });
              if (paymentResult.success) window.location.href = "/dashboard";
            }}
            sx={{
              textTransform: "none", borderRadius: 2, fontWeight: 700, fontSize: 14,
              background: paymentResult.success ? "linear-gradient(135deg, #16a34a, #22c55e)" : "linear-gradient(135deg, #dc2626, #b91c1c)",
            }}
          >
            {paymentResult.success ? "Back to Dashboard" : "Try Again"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}